'use client'

import { useStoreI18n } from '@/store'
import {
  Typo3EventDetailContact,
  Typo3EventDetailItem
} from '@/types/typo3/sections/spotsanity-eventlist/detail'
import Button from '@/ui/components/Button/Button'

import { memo, useMemo } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data: Typo3EventDetailItem
}

interface ContactProps {
  title: string
  contact: Typo3EventDetailContact
}

const EventContact = ({ data }: Props) => {
  const D = useStoreI18n.use.dictionary()

  const getContacts = useMemo(
    () =>
      (
        title: string,
        contact?: Typo3EventDetailContact
      ): ContactProps | null => {
        if (contact) {
          return {
            title,
            contact
          }
        }
        return null
      },
    []
  )

  const contacts = [
    getContacts(D['fe.contact.booking.title'], data?.bookingContact),
    getContacts(D['fe.contact.event.title'], data?.eventContact),
    getContacts(D['fe.contact.info.title'], data?.infoContact),
    getContacts(D['fe.contact.organizer.title'], data?.organizerContact)
  ]

  const mergedContacts = contacts.reduce(
    (acc: ContactProps[], curr: ContactProps | null) => {
      if (curr) {
        const existingContact = acc.find(
          (contact) => contact.contact.uid === curr.contact.uid
        )
        if (existingContact) {
          existingContact.title += ` / ${curr.title}`
        } else {
          acc.push(curr)
        }
      }
      return acc
    },
    []
  )

  return (
    <>
      {mergedContacts.map((item) => (
        <div key={item.contact?.uid} className={'body_el'}>
          <p className="textstyle-md-bold mb-2xs">{item?.title}</p>
          <div className={'body_content'}>
            <p>{item?.contact?.company}</p>
            <p>{item?.contact?.street}</p>
            <p>{`${item?.contact?.zipcode} ${item?.contact?.city} - ${item?.contact?.country}`}</p>
            <div className="mt-sm gap-y-2xs flex flex-col items-start justify-start">
              {item?.contact?.phone && (
                <Button
                  variant="text"
                  nextLink={false}
                  href={`tel:${item?.contact?.phone}`}
                  size="s"
                >
                  {item?.contact?.phone}
                </Button>
              )}
              {item?.contact?.email && (
                <Button
                  variant="text"
                  nextLink={false}
                  href={`mailto:${item?.contact?.email}`}
                  size="s"
                >
                  {item?.contact?.email}
                </Button>
              )}
              {item?.contact?.website && (
                <Button
                  variant="text"
                  nextLink={false}
                  target="_blank"
                  href={item?.contact?.website}
                  size="s"
                >
                  {item?.contact?.website}
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

export default memo(EventContact)
